import React, { useState, useEffect } from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Badge } from '@voilajsx/uikit/badge';
import { Input } from '@voilajsx/uikit/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@voilajsx/uikit/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@voilajsx/uikit/dialog';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Label } from '@voilajsx/uikit/label';
import { CreditCard, Plus, Edit, Loader2, Shield } from 'lucide-react';
import { AdminSidebar } from '../components/AdminSidebar';
import { Header,  SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { route } from '../../../shared/utils';
import { config } from '../../auth/config';

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  priceInr: number;
  status: 'active' | 'inactive';
}

interface PackForm {
  name: string;
  credits: number;
  priceInr: number;
}

const emptyForm: PackForm = { name: '', credits: 0, priceInr: 0 };

const AdminPacksPage: React.FC = () => {
  const { user, token } = useAuth();

  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPack, setEditingPack] = useState<CreditPack | null>(null);
  const [form, setForm] = useState<PackForm>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const isAdminSystem = user?.role === 'admin' && user?.level === 'system';

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
    [config.auth.headers.auth]: `Bearer ${token}`,
  });

  const fetchPacks = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/payment/admin/packs`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      setPacks(data.data?.packs || data.packs || data.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch credit packs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdminSystem && token) fetchPacks();
  }, [token, isAdminSystem]);

  if (!user) return null;

  const openCreateDialog = () => {
    setEditingPack(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (pack: CreditPack) => {
    setEditingPack(pack);
    setForm({ name: pack.name, credits: pack.credits, priceInr: pack.priceInr });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const isEdit = !!editingPack;
      const url = isEdit
        ? `${config.api.baseUrl}/api/payment/admin/packs/${editingPack!.id}`
        : `${config.api.baseUrl}/api/payment/admin/packs`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${res.status}: ${res.statusText}`);
      }

      setSuccess(isEdit ? 'Credit pack updated successfully' : 'Credit pack created successfully');
      setDialogOpen(false);
      fetchPacks();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save credit pack');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStatus = async (pack: CreditPack) => {
    const newStatus = pack.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`${config.api.baseUrl}/api/payment/admin/packs/${pack.id}/status`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed to toggle status');
      setSuccess(`Pack ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchPacks();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (!isAdminSystem) {
    return (
      <PageLayout size="full">
        <SEO title="Access Denied" description="You don't have permission to access this page" />
        <Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Shield className="h-12 w-12 mx-auto mb-4 text-destructive" />
                  <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
                  <p className="text-muted-foreground mb-4">Only system administrators can manage credit packs.</p>
                  <Button asChild><a href={route('/admin')}>Back to Admin</a></Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  return (
    <PageLayout size="full">
      <SEO title="Credit Packs" description="Manage credit pack offerings" />
      <Header />

      <PageLayout.Content>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <AdminSidebar />
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 72 }}>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Credit Packs</h1>
              <p className="text-muted-foreground">Manage credit pack offerings and pricing</p>
            </div>
            <Button onClick={openCreateDialog} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Pack
            </Button>
          </div>

          {success && (
            <Alert className="bg-green-50 border-green-200 text-green-800">
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert className="bg-destructive/10 border-destructive text-destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Credit Packs ({packs.length})
              </CardTitle>
              <CardDescription>Available credit packs for purchase</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-2 py-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-12 rounded-md bg-zinc-800 animate-pulse" />
                  ))}
                </div>
              ) : packs.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-2">No credit packs found</p>
                  <p className="text-muted-foreground">Create a credit pack to get started.</p>
                </div>
              ) : (
                <div className="rounded-md border border-zinc-700">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-zinc-700">
                        <TableHead className="border-r border-zinc-700">Name</TableHead>
                        <TableHead className="border-r border-zinc-700">Amount (₹)</TableHead>
                        <TableHead className="border-r border-zinc-700">Status</TableHead>
                        <TableHead className="w-[150px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {packs.map((pack) => (
                        <TableRow key={pack.id} className="border-b border-zinc-700">
                          <TableCell className="border-r border-zinc-700 font-medium">{pack.name}</TableCell>
                          <TableCell className="border-r border-zinc-700 font-bold text-[#FFD93D]">{`₹${pack.credits.toLocaleString()}`}</TableCell>
                          <TableCell className="border-r border-zinc-700">
                            <Badge variant={pack.status === 'active' ? 'default' : 'destructive'}>
                              {pack.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Edit" onClick={() => openEditDialog(pack)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => toggleStatus(pack)} className="text-xs px-2">
                                {pack.status === 'active' ? 'Deactivate' : 'Activate'}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
          </div>
        </div>
      </PageLayout.Content>


      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-[#1e1e1e] border-zinc-700 text-foreground">
          <DialogHeader>
            <DialogTitle>{editingPack ? 'Edit Credit Pack' : 'Create Credit Pack'}</DialogTitle>
            <DialogDescription>
              {editingPack ? 'Update the credit pack details.' : 'Define a new credit pack for users to purchase.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Pack Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Starter Pack"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="credits">Amount (₹)</Label>
              <Input
                id="credits"
                type="number"
                value={form.credits}
                onChange={(e) => setForm({ ...form, credits: Number(e.target.value), priceInr: Number(e.target.value) })}
                placeholder="e.g., 500"
              />
              <p className="text-xs text-muted-foreground">₹500 = 500 credits. Amount paid = credits received.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSaving}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving} className="gap-2">
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editingPack ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminPacksPage;
